import {
    all,
    call,
    put,
    select,
    cancelled,
    takeLatest,
} from 'redux-saga/effects';
import { delay } from 'redux-saga';
import urlService from 'common/services/api/urlService';
import { tl } from 'common/services/i18nService';
import { pushAlert } from 'common/components/notifications/actions';
import { AlertLevel } from 'common/components/notifications/types';
import { ContextState } from '../context/reducer';
import { getExternalSystemName, SyncClickActions } from '../../components/header/services/externalSystemService';
import apiService from '../../services/api/apiService';
import { transferGradesLoading, syncWithExternalTestLoading } from './actions';
import { OptionsMenuActionsName } from '../optionsMenu/types';
import { onMenuItemClick } from '../optionsMenu/actions';
import { updateGradingInfo } from '../assessment/actions';
import { ExternalSystem } from '../common/constants';


const getContextState = (state: { context: any }) => state.context;
const getState = (state: { context: any }) => state;

const createdUrlFilters = (event: string[], contextObjectId: number) => {
    const commonFilters = {
        page: 1,
        hitsPerPage: 10,
    };
    return encodeURIComponent(
        JSON.stringify({
            ...commonFilters,
            filters: {
                event,
                eventType: '$all',
                contextObjectId,
            },
        })
    );
};

function* callOpenNewTabFromPlatform(actionName: string) {
    const platformAdminUrl = urlService.prependDomain('/admin#');
    const contextState: ContextState = yield select(getContextState);
    const { assessmentRunId } = contextState;
    const platformDeliveryUrl = `${platformAdminUrl}deliver-test/${assessmentRunId}`;
    let urlLog: string;
    let events: string[];
    switch (actionName) {
    case SyncClickActions.SYNC_SETTINGS:
        window.open(`${platformDeliveryUrl}?openFSModal=true`);
        break;
    case SyncClickActions.SYNC_SYNCHRONISATION_LOG:
        events = [
            'synchronized_with_external_system',
            'external_test_sync_failed',
            'external_test_auto_sync',
        ];
        urlLog = createdUrlFilters(events, contextState.assessmentRunId);
        window.open(`${platformAdminUrl}log/data/${urlLog}`);
        break;
    case SyncClickActions.SYNC_ERROR_LOG:
        events = ['external_test_sync_failed'];
        urlLog = createdUrlFilters(events, contextState.assessmentRunId);
        window.open(`${platformAdminUrl}log/data/${urlLog}`);
        break;
    case SyncClickActions.TRANSFER_LOG:
        events = ['grades_transferred', 'grades_transferred_for_committee'];
        urlLog = createdUrlFilters(events, contextState.assessmentRunId);
        window.open(`${platformAdminUrl}log/data/${urlLog}`);
        break;
    case SyncClickActions.TRANSFER_LOG_ERRORS:
        events = ['grades_not_transferred', 'external_test_sync_failed'];
        urlLog = createdUrlFilters(events, contextState.assessmentRunId);
        window.open(`${platformAdminUrl}log/data/${urlLog}`);
        break;
    }
}

function* callTransferGrades(actionName: string) {
    const controller = new AbortController();
    const { assessment, context, session } = yield select(getState);
    const { assessmentRunId, selectedCommitteeId } = context;
    const { externalSystem } = assessment;
    let alertProps;
    const committeeId = actionName !== SyncClickActions.TRANSFER_GRADES_TO_EXTERNAL_SYSTEM
        ? selectedCommitteeId : null;
    const transferExternalSystem = getExternalSystemName(externalSystem);

    function* alertSuccessCallback() {
        alertProps = {
            key: tl('grading.header.syncTransfer.completed'),
            info: tl('grading.header.syncTransfer.completedText', {
                externalSystem: transferExternalSystem,
            }),
            level: AlertLevel.SUCCESS,
            payload: {},
        };
        yield put(pushAlert(alertProps));
        yield put(updateGradingInfo(session.user.id));
    }
    function* alertFailCallback() {
        alertProps = {
            key: tl('grading.header.syncTransfer.failed'),
            info: '',
            level: AlertLevel.ERROR,
            payload: {},
        };
        yield put(pushAlert(alertProps));
    }

    try {
        yield put(transferGradesLoading(true));
        alertProps = {
            key: tl('grading.header.syncTransfer.transferStart'),
            info: tl('grading.header.syncTransfer.connectingToExternalSystem', {
                externalSystem: transferExternalSystem,
            }),
            level: AlertLevel.INFO,
            payload: {},
        };
        yield put(pushAlert(alertProps));
        if (transferExternalSystem === ExternalSystem.LADOK) {
            const transferTaskData = yield call(
                apiService.transferGradesToLadok,
                assessmentRunId,
                controller.signal,
            );
            let data;
            while (true) {
                data = yield call(
                    apiService.checkStatus,
                    transferTaskData.jobId,
                    controller.signal,
                );
                if (data.progress === 100) {
                    break;
                } else {
                    // delay checkStatus by 5 seconds
                    yield delay(5000);
                }
            }
            if (data.status === 'success') {
                yield alertSuccessCallback();
            } else {
                // make prod debug easier by showing the error status in the console
                console.log(`transfer to Ladok failed with status: ${data.status}`);
                yield alertFailCallback();
            }
        } else {
            yield call(
                apiService.transferGradesToExternalSystem,
                assessmentRunId,
                false,
                committeeId,
                controller.signal,
            );
            yield alertSuccessCallback();
        }
    } catch (e) {
        console.error(e.message);
        yield alertFailCallback();
    } finally {
        yield put(transferGradesLoading(false));
        if (yield cancelled()) {
            controller.abort();
        }
    }
}

function* syncWithExternalTest() {
    const { context, session } = yield select(getState);
    const events = ['external_test_sync_failed'];
    const urlLog = createdUrlFilters(events, context.assessmentRunId);
    let alertProps;

    try {
        yield put(syncWithExternalTestLoading(true));
        const res = yield call(
            apiService.syncWithExternalTest,
            context.assessmentRunId
        );
        yield put(updateGradingInfo(session.user.id));
        if (res.error) {
            alertProps = {
                key: tl('grading.header.syncError'),
                info: '',
                level: AlertLevel.WARNING,
                payload: {},
                link: {
                    text: tl('grading.header.syncViewDetails'),
                    url: `/admin#log/data/${urlLog}`,
                },
            };
        } else {
            const { assessment } = yield select(getState);
            const {
                externalSystem,
                totalNumberOfCandidates,
                totalNumberOfEvaluators,
                committees,
            } = assessment;
            alertProps = {
                key: tl('grading.header.syncComplete', { externalSystem }),
                info: tl('grading.header.syncCompleteData', {
                    numberOfCandidates: totalNumberOfCandidates,
                    numberOfGraders: totalNumberOfEvaluators,
                    numberOfCommittees: committees.length,
                }),
                level: AlertLevel.SUCCESS,
                payload: {},
            };
        }
        yield put(syncWithExternalTestLoading(false));
        yield put(pushAlert(alertProps, res.error));
    } catch (error) {
        yield put(syncWithExternalTestLoading(false));
        yield put({ error, type: 'CANT_SYNC' });
    }
}

export function* callOnSyncActionClick(action: any) {
    const actionName: string = action.payload;
    switch (actionName) {
    case SyncClickActions.SYNC_SETTINGS:
    case SyncClickActions.SYNC_SYNCHRONISATION_LOG:
    case SyncClickActions.SYNC_ERROR_LOG:
    case SyncClickActions.TRANSFER_LOG:
    case SyncClickActions.TRANSFER_LOG_ERRORS:
        yield call(callOpenNewTabFromPlatform, actionName);
        break;
    case SyncClickActions.TRANSFER_GRADES_FROM_COMMITTEE:
    case SyncClickActions.TRANSFER_GRADES_TO_EXTERNAL_SYSTEM:
        yield call(callTransferGrades, actionName);
        break;
    case SyncClickActions.SYNC_UPDATE:
        yield call(syncWithExternalTest);
        break;
    case SyncClickActions.OPEN_COMMITTEE_SELECTOR:
        yield put(onMenuItemClick(OptionsMenuActionsName.OPEN_COMMITTEE_SELECTOR));
        break;
    }
}

// Watchers
function* watchOnSyncActionClick() {
    yield takeLatest(
        [SyncClickActions.SYNC_ACTION_CLICKED],
        callOnSyncActionClick
    );
}

export function* rootSyncActionsSaga() {
    yield all([watchOnSyncActionClick()]);
}
